import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Box, Typography, Paper, Skeleton, Table, TableHead, TableRow, TableCell,
  TableBody, Chip, Divider, Tooltip, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Switch, FormControlLabel, Alert, Stack,
} from '@mui/material';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import {
  getInferenceBalance,
  fundInferenceBalance,
  updateInferenceAutoRecharge,
} from '../api/developerApi';
import ApiErrorAlert from './ApiErrorAlert';
import SectionHeader from './SectionHeader';
import { assertHttps } from '../utils/assertHttps';
import { fmt } from '../utils/fmt';

// ===========================================================================
// InferenceBalanceCard
//
// Shows the partner's pre-paid inference balance (balanceCentsMilli: thousandths of a cent) plus the
// per-endpoint inference activity from the usage report. Two actions:
//   1. "Add funds" — opens a dialog to top up via Stripe Checkout (paid plan only)
//   2. "Auto-recharge" — opens a settings dialog to enable/configure
//
// Both actions are gated on `canFund` returned by GET /developer/billing/inference
// (paid plan + Stripe customer on file). Free-tier partners start at $0.00 (the
// unconditional 100¢ provision seed was removed, #319) and get both buttons disabled,
// each with a tooltip saying why — they fund inference only on a paid plan. The plan and
// `canFund` exist only once that balance response has arrived: until then, and if it
// failed, the card shows no plan-dependent copy and no balance figure.
// ===========================================================================
// `usage` and `paidUpgradeEnabled` come from the billing response the page loads (the card does
// not load it). While that response is loading (`billingLoading`) the activity rows show
// placeholders, not zeros; if it failed (`billingFailed`) they show a dash. `paidUpgradeEnabled`
// decides the free-plan copy: while it is off the portal offers no upgrade, so the copy must not
// tell the user to upgrade (an absent flag in a loaded response counts as off). Until the response
// has loaded, and if it failed, the flag is unknown, so the card says nothing about upgrading.
// A disabled button's reason is shown by a tooltip on its wrapper. The wrapper is a named keyboard stop only when
// there is a reason to give; with none (the facts are not in yet) it must not be a nameless stop.
const reasonProps = (title) => (title ? { tabIndex: 0, role: 'group' } : {});

const AUTO_RECHARGE_REASON = 'Auto-recharge requires a paid plan and saved payment method';

export default function InferenceBalanceCard({
  usage, billingLoading = false, billingFailed = false, paidUpgradeEnabled = false,
}) {
  const queryClient = useQueryClient();
  const { data: inf, isLoading, error: balanceError } = useQuery('inferenceBalance', getInferenceBalance);

  const [fundOpen, setFundOpen]       = useState(false);
  const [autoOpen, setAutoOpen]       = useState(false);

  const inference = usage?.inference;
  const endpoints = inference?.endpoints ?? {};
  const totals    = inference?.totals    ?? {};

  // The balance response has arrived: the plan claims below wait for it.
  const balanceKnown = inf !== undefined;
  // The figure itself: a response without the milli field (an API older than this page, during a deploy) shows
  // "balance unavailable", never $0.00, which would prompt a needless top-up.
  const amountKnown = Number.isInteger(inf?.balanceCentsMilli);
  // The balance is exact milli-cents (1 cent = 1000). Dollars are shown floored to the whole cent, so the
  // card never shows more than is available; the caption gives the exact figure in cents.
  const balanceMilli = amountKnown ? inf.balanceCentsMilli : 0;
  const balanceDollars = (Math.floor(balanceMilli / 1000) / 100).toFixed(2);
  const balanceCentsExact = (balanceMilli / 1000).toLocaleString(undefined, { maximumFractionDigits: 3 });
  const auto         = inf?.autoRecharge ?? {};
  const canFund      = inf?.canFund ?? false;
  const plan         = inf?.plan ?? 'free';
  const upgradeKnown = !billingLoading && !billingFailed;
  // Like the Add funds reason, this one is a claim about the plan, so it waits for the balance response.
  const autoRechargeTooltip = balanceKnown ? AUTO_RECHARGE_REASON : '';
  let addFundsTooltip = '';
  if (balanceKnown) {
    if (plan !== 'free') addFundsTooltip = 'Set up a payment method to fund manually';
    else if (upgradeKnown) addFundsTooltip = paidUpgradeEnabled
      ? 'Upgrade to Starter to fund manually'
      : "Funding isn't available on the free plan";
  }

  return (
    <Paper elevation={0} sx={{ border: '1px solid #E4E4E7', borderRadius: 2, mb: 3 }}>
      {balanceError && (
        <Box sx={{ p: 2.5, pb: 0 }}>
          <ApiErrorAlert error={balanceError} />
        </Box>
      )}
      <Box sx={{ p: 2.5, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <SectionHeader title="Inference balance" tooltip="Pre-paid ledger that absorbs the token-cost portion of inference calls. Separate from your monthly plan allowance, which covers the per-call platform overhead." />
        </Box>
        <Stack direction="row" spacing={1}>
          {canFund
            ? <Button size="small" variant="outlined" onClick={() => setFundOpen(true)}>Add funds</Button>
            : <Tooltip title={addFundsTooltip}>
                <span {...reasonProps(addFundsTooltip)}><Button size="small" variant="outlined" disabled>Add funds</Button></span>
              </Tooltip>}
          {canFund
            ? <Button size="small" variant="outlined" onClick={() => setAutoOpen(true)}>
                Auto-recharge {auto.enabled ? '(on)' : ''}
              </Button>
            : <Tooltip title={autoRechargeTooltip}>
                <span {...reasonProps(autoRechargeTooltip)}><Button size="small" variant="outlined" disabled>Auto-recharge</Button></span>
              </Tooltip>}
        </Stack>
      </Box>

      <Box sx={{ px: 2.5, pb: 2 }}>
        {isLoading
          ? <Skeleton width={160} height={40} />
          : <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Typography variant="h4" fontWeight={700}>
                {amountKnown ? `$${balanceDollars}` : '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {amountKnown ? `(${balanceCentsExact}¢ available)` : '(balance unavailable)'}
              </Typography>
              {auto.enabled && (
                <Chip
                  label={`Auto-recharge: $${((auto.amountCents ?? 0) / 100).toFixed(2)} when below $${((auto.thresholdCents ?? 0) / 100).toFixed(2)}`}
                  size="small"
                  sx={{ ml: 1, bgcolor: '#ECFDF5', color: '#065F46', fontWeight: 600, fontSize: 11 }}
                />
              )}
            </Box>}
        {auto.enabled && auto.thresholdCents > 0 && auto.amountCents > 0 && balanceMilli < auto.thresholdCents * 1000 * 1.25 && (
          <Alert severity="info" sx={{ mt: 1.5, fontSize: 12 }}>
            Your balance is close to the auto-recharge threshold. The next inference call may trigger a charge of ${((auto.amountCents) / 100).toFixed(2)}.
          </Alert>
        )}
        {!isLoading && balanceKnown && upgradeKnown && !canFund && plan === 'free' && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            {paidUpgradeEnabled
              ? <>The free plan does not include a pre-paid inference balance and cannot be funded.
                Upgrade to Starter to add funds or enable auto-recharge.</>
              : <>The free plan does not include a pre-paid inference balance, and funding
                isn&apos;t available on the free plan.</>}
          </Typography>
        )}
      </Box>

      {/* Per-endpoint inference activity for the current month */}
      <Divider />
      <Table size="small">
        <TableHead sx={{ bgcolor: 'background.default' }}>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Endpoint</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12 }}>Calls</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12 }}>Input tokens</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, fontSize: 12 }}>Output tokens</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <InferenceRow label="POST /v1/chat"               data={endpoints.chat} loading={isLoading || billingLoading} failed={billingFailed} />
          <InferenceRow label="POST /v1/rag"                data={endpoints.rag}  loading={isLoading || billingLoading} failed={billingFailed} />
          <InferenceRow label="POST /v1/documents/{id}/ask" data={endpoints.ask}  loading={isLoading || billingLoading} failed={billingFailed} />
          <TableRow sx={{ '& td': { fontWeight: 600, bgcolor: '#FAFAFA' } }}>
            <TableCell sx={{ fontSize: 13 }}>Total</TableCell>
            <TableCell align="right" sx={{ fontSize: 13, fontFamily: 'monospace' }}>{totalCell(fmt(totals.calls), billingLoading, billingFailed)}</TableCell>
            <TableCell align="right" sx={{ fontSize: 13, fontFamily: 'monospace' }}>{totalCell(fmt(totals.inputTokens), billingLoading, billingFailed)}</TableCell>
            <TableCell align="right" sx={{ fontSize: 13, fontFamily: 'monospace' }}>{totalCell(fmt(totals.outputTokens), billingLoading, billingFailed)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <FundDialog
        open={fundOpen}
        onClose={() => setFundOpen(false)}
      />
      <AutoRechargeDialog
        open={autoOpen}
        onClose={() => setAutoOpen(false)}
        current={auto}
        onSaved={() => queryClient.invalidateQueries('inferenceBalance')}
      />
    </Paper>
  );
}

// A cell of the Total row: a placeholder while the billing response loads, a dash if it failed.
function totalCell(value, loading, failed) {
  if (loading) return <Skeleton />;
  return failed ? '—' : value;
}

function InferenceRow({ label, data, loading, failed }) {
  if (loading) {
    return (
      <TableRow>
        <TableCell><Skeleton /></TableCell>
        <TableCell><Skeleton /></TableCell>
        <TableCell><Skeleton /></TableCell>
        <TableCell><Skeleton /></TableCell>
      </TableRow>
    );
  }
  const d = data ?? {};
  const show = (n) => (failed ? '—' : fmt(n));
  return (
    <TableRow hover>
      <TableCell sx={{ fontSize: 13, fontFamily: 'monospace' }}>{label}</TableCell>
      <TableCell align="right" sx={{ fontSize: 13, fontFamily: 'monospace' }}>{show(d.calls)}</TableCell>
      <TableCell align="right" sx={{ fontSize: 13, fontFamily: 'monospace' }}>{show(d.inputTokens)}</TableCell>
      <TableCell align="right" sx={{ fontSize: 13, fontFamily: 'monospace' }}>{show(d.outputTokens)}</TableCell>
    </TableRow>
  );
}

function FundDialog({ open, onClose }) {
  const [amount, setAmount] = useState('25.00'); // dollars
  const [error, setError]   = useState('');

  const fund = useMutation(
    (amountCents) => fundInferenceBalance(amountCents),
    {
      onSuccess: (data) => {
        if (!data?.url) return;
        try {
          window.location.href = assertHttps(data.url, 'checkout link');
        } catch (e) {
          setError(e.message);
        }
      },
      onError:   (e)    => { setError(e?.response?.data?.message || 'Failed to start checkout'); },
    }
  );

  const handleFund = () => {
    setError('');
    const dollars = parseFloat(amount);
    if (Number.isNaN(dollars) || dollars < 1 || dollars > 10000) {
      setError('Amount must be between $1 and $10,000');
      return;
    }
    fund.mutate(Math.round(dollars * 100));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add funds to inference balance</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" mb={2}>
          You'll be redirected to Stripe Checkout to complete payment. The amount is added to your inference balance once the payment has settled: card payments usually settle within moments, bank debits can take several days.
        </Typography>
        <TextField
          label="Amount (USD)"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          fullWidth
          autoFocus
          inputProps={{ inputMode: 'decimal' }}
          helperText="Minimum $1.00, maximum $10,000.00"
        />
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleFund} variant="contained" disabled={fund.isLoading}>
          {fund.isLoading ? 'Redirecting…' : 'Continue to Stripe'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AutoRechargeDialog({ open, onClose, current, onSaved }) {
  const [enabled, setEnabled]     = useState(false);
  const [amount, setAmount]       = useState('50.00');
  const [threshold, setThreshold] = useState('10.00');
  const [error, setError]         = useState('');

  React.useEffect(() => {
    if (open) {
      setEnabled(Boolean(current?.enabled));
      // `!= null`, not truthiness: a saved threshold of $0 is a real setting and must not be replaced by the default
      if (current?.amountCents != null)    setAmount((current.amountCents / 100).toFixed(2));
      if (current?.thresholdCents != null) setThreshold((current.thresholdCents / 100).toFixed(2));
      setError('');
    }
  }, [open, current]);

  const save = useMutation(
    (body) => updateInferenceAutoRecharge(body),
    {
      onSuccess: () => { onSaved && onSaved(); onClose(); },
      onError:   (e) => { setError(e?.response?.data?.message || 'Failed to save settings'); },
    }
  );

  const handleSave = () => {
    setError('');
    if (!enabled) { save.mutate({ enabled: false }); return; }
    const amt = parseFloat(amount);
    const thr = parseFloat(threshold);
    if (Number.isNaN(amt) || amt < 10 || amt > 10000) {
      setError('Amount must be between $10 and $10,000'); return;
    }
    if (Number.isNaN(thr) || thr < 0 || thr >= amt) {
      setError('Threshold must be ≥ $0 and < amount (otherwise auto-recharge would loop)'); return;
    }
    save.mutate({
      enabled: true,
      amountCents:    Math.round(amt * 100),
      thresholdCents: Math.round(thr * 100),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Auto-recharge inference balance</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" mb={2}>
          When your inference balance drops below the threshold, we'll automatically charge your saved payment method for the configured amount.
        </Typography>
        <FormControlLabel
          control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
          label={enabled ? 'Auto-recharge enabled' : 'Auto-recharge disabled'}
        />
        {enabled && (
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              label="Recharge amount (USD)"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              fullWidth
              inputProps={{ inputMode: 'decimal' }}
              helperText="How much to add when triggered. Min $10, max $10,000."
            />
            <TextField
              label="Threshold (USD)"
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              fullWidth
              inputProps={{ inputMode: 'decimal' }}
              helperText="Trigger when balance drops below this. Must be less than recharge amount."
            />
            {parseFloat(threshold) > 0 && parseFloat(amount) > parseFloat(threshold)
              && parseFloat(threshold) / parseFloat(amount) >= 0.8 && (
              <Alert severity="warning" sx={{ fontSize: 12 }}>
                A threshold of {Math.round(parseFloat(threshold) / parseFloat(amount) * 100)}% of the recharge amount risks frequent charges. Consider lowering the threshold.
              </Alert>
            )}
          </Stack>
        )}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={save.isLoading}>
          {save.isLoading ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
